import type { FieldDataType, FilterableFieldDto } from "./api";

/**
 * OPERATORS_BY_TYPE is a UI-only concern (which operators a data_type
 * offers in the condition builder) -- the field registry itself (labels/
 * kinds/data types) is fetched live from GET /filterable-fields
 * (app/api/routes/fields.py), never mirrored statically here.
 */

export const OPERATORS_BY_TYPE: Record<FieldDataType, { value: string; label: string }[]> = {
  number: [
    { value: "gt", label: ">" },
    { value: "gte", label: ">=" },
    { value: "lt", label: "<" },
    { value: "lte", label: "<=" },
    { value: "eq", label: "=" },
  ],
  percent: [
    { value: "gt", label: ">" },
    { value: "gte", label: ">=" },
    { value: "lt", label: "<" },
    { value: "lte", label: "<=" },
    { value: "eq", label: "=" },
  ],
  enum: [
    { value: "eq", label: "=" },
    { value: "in", label: "in" },
  ],
  text: [{ value: "eq", label: "=" }],
  bool: [{ value: "eq", label: "=" }],
};

export interface ColumnDef {
  key: string;
  label: string;
  group: "Identity" | "Price" | "Technical" | "Model" | "Flow";
  defaultVisible: boolean;
  /** true for a real filterable_fields row (any `computed` feature) whose
   * value lives in StockRow.features[key]; false for the small set of
   * dedicated/synthetic columns (signal, ticker, sector, cap, close) that
   * have their own named StockRow field or are computed client-side. */
  isFeature: boolean;
}

const DEDICATED_COLUMNS: ColumnDef[] = [
  // DUAL-WEIGHT SIGNAL (2026-09-21): the single "signal" column is replaced
  // by TWO, per the approved mockup (Option A -- both always shown, neither
  // hidden). "signal" itself stays a valid (sort-only, legacy) StockRow
  // field/API sort key for back-compat, but is no longer offered as a
  // column -- every value it carried is now under signal_equal instead.
  { key: "signal_equal", label: "Signal (equal-weight)", group: "Model", defaultVisible: true, isFeature: false },
  { key: "signal_skill_weighted", label: "Signal · skill (shrunk IC-weight)", group: "Model", defaultVisible: true, isFeature: false },
  { key: "display_ticker", label: "Ticker", group: "Identity", defaultVisible: true, isFeature: false },
  { key: "sector_key", label: "Sector", group: "Identity", defaultVisible: true, isFeature: false },
  { key: "cap_tier", label: "Cap tier", group: "Identity", defaultVisible: true, isFeature: false },
  { key: "close", label: "Close", group: "Price", defaultVisible: true, isFeature: false },
  // Relative CAPE band (2026-09-27): daily-cached in stock_cape_snapshot
  // (app/factory/stock_cape.py) -- never computed live per screener row
  // (~0.45s/stock would make a filtered page take seconds). NULL for the
  // ~91% of stocks without 10yr+ confirmed earnings history, so NOT
  // default-visible (would arrive mostly blank) -- same precedent as the
  // sparse Flow columns below.
  { key: "stock_relative_cape", label: "Relative CAPE", group: "Model", defaultVisible: false, isFeature: false },
  { key: "stock_cape_classification", label: "Relative CAPE vs history", group: "Model", defaultVisible: false, isFeature: false },
];

// A stored feature visible by default even before the user asks for it --
// keeps the table from arriving with only 5 columns. Everything else in
// the registry (30 more as of the 2026-09-02 seeding) starts hidden but
// selectable, and anything referenced in a filter condition is force-shown
// regardless of this list (see StocksPage's effectiveVisibleCols).
const DEFAULT_VISIBLE_FEATURES = new Set(["rsi_14"]);

/**
 * Builds the full, LIVE column list: the 5 dedicated columns above, plus
 * one Technical-group column per `computed` field the registry actually
 * has (applies_to includes 'stock') -- so a newly-seeded feature shows up
 * in the Columns menu the moment it exists in `filterable_fields`,
 * without a frontend change. Replaces the old static STOCK_COLUMNS.
 */
export function buildStockColumns(fields: FilterableFieldDto[]): ColumnDef[] {
  const featureColumns: ColumnDef[] = fields
    .filter((f) => f.source === "computed" && f.applies_to.includes("stock"))
    .map((f) => ({
      key: f.field_key,
      label: f.label,
      group: "Technical" as const,
      defaultVisible: DEFAULT_VISIBLE_FEATURES.has(f.field_key),
      isFeature: true,
    }));
  return [...DEDICATED_COLUMNS, ...featureColumns];
}

const INDEX_DEDICATED_COLUMNS: ColumnDef[] = [
  { key: "sigma", label: "Vol σ (real)", group: "Model", defaultVisible: true, isFeature: false },
  { key: "display_ticker", label: "Index", group: "Identity", defaultVisible: true, isFeature: false },
  { key: "close", label: "Level", group: "Price", defaultVisible: true, isFeature: false },
  { key: "day_change_pct", label: "Chg %", group: "Price", defaultVisible: true, isFeature: false },
  // % of the index's OWN constituent stocks advancing today (stock_segment_
  // membership's index_grp dimension, newly aggregated) -- a real
  // participation read, not a model output; null for an index with no
  // constituent mapping at all (SENSEX), never a fabricated 0%/100%.
  { key: "constituent_breadth", label: "Breadth", group: "Price", defaultVisible: true, isFeature: false },
  // Real NSE Indices P/E vs this index's OWN post-2021 history -- see
  // app/factory/index_valuation_view.py. Honest-empty for IDX:SENSEX (no
  // NSE Indices source), never a fabricated band.
  { key: "valuation", label: "P/E vs history", group: "Price", defaultVisible: true, isFeature: false },
];

/** Same live-registry principle as buildStockColumns, scoped to
 * applies_to='index' -- and NO "signal"/model-direction column at all
 * (there is no directional index signal to show; see market.py's
 * NO_DIRECTIONAL_SIGNAL_NOTE). "sigma" replaces it as the one real
 * model-derived column an index has. */
export function buildIndexColumns(fields: FilterableFieldDto[]): ColumnDef[] {
  const featureColumns: ColumnDef[] = fields
    .filter((f) => f.source === "computed" && f.applies_to.includes("index"))
    .map((f) => ({
      key: f.field_key,
      label: f.label,
      group: "Technical" as const,
      defaultVisible: DEFAULT_VISIBLE_FEATURES.has(f.field_key),
      isFeature: true,
    }));
  // fii_net/dii_net (2026-09-06): real filterable_fields rows, but
  // source='column' (a dedicated table, not feature_values) rather than
  // 'computed' -- listed explicitly by key instead of widening the generic
  // filter above, so a future non-flow 'column'-sourced field (identity/
  // fact columns already rendered as dedicated columns, e.g. close/
  // sector_key) doesn't silently double up as a feature column too.
  const flowKeys = new Set(["fii_net", "dii_net"]);
  const flowColumns: ColumnDef[] = fields
    .filter((f) => flowKeys.has(f.field_key) && f.applies_to.includes("index"))
    .map((f) => ({
      key: f.field_key, label: f.label, group: "Flow" as const,
      defaultVisible: false, isFeature: true,
    }));
  return [...INDEX_DEDICATED_COLUMNS, ...featureColumns, ...flowColumns];
}
